import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  Label,
  Input,
  FormFeedback,
  Alert,
  Row,
  Col,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';

import { updateLead } from '../../../../redux/actions/lead';
import { useCompletedValidation } from './formik';
import { PAYMENT_METHOD_OPTIONS, DOCUMENT_TYPE_OPTIONS } from '../../constants';
import { formatFormCurrency } from '../../../../utils/utils';

function CompletedForm({ leadId, leadOrder, onSubmit, onCancel }) {
  const dispatch = useDispatch();
  const updateState = useSelector((state) => state.lead.update);
  const [modal, setModal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const formik = useCompletedValidation((values) => {
    setLoading(true);
    setErrorMessage('');

    if (!leadId) {
      setErrorMessage('No se puede procesar: ID de lead no válido.');
      setLoading(false);
      return;
    }

    const leadInfo = {
      state: 'completed',
      completedDate: values.date,
      completedPrice: values.price,
      completedPaymentMethod: values.paymentMethod,
      completedComment: values.comment,
      completedComisionCliente: values.comisionCliente,
      completedComisionProveedor: values.comisionProveedor,
      completedValorNeto: values.valorNeto,
      completedGastoTransferencia: values.gastoTransferencia,
      completedImpuestoTransferencia: values.impuestoTransferencia,
      completedIva: values.iva,
      completedTag: values.tag,
      completedDocumento: values.documento,
      order: leadOrder,
    };

    dispatch(updateLead(leadId, leadInfo));
  });

  const toggle = (operation) => {
    setModal(!modal);
    if (operation === 'submit') {
      onSubmit();
    } else {
      onCancel();
    }
  };

  useEffect(() => {
    if (updateState) {
      setLoading(false);

      if (updateState.status === 'success') {
        setModal(false);
        onSubmit();
      } else if (updateState.status === 'danger') {
        setErrorMessage(updateState.message || 'Error al completar el lead');
      }
    }
  }, [updateState, onSubmit]);

  const renderCurrencyInput = (name, label, placeholder) => (
    <section className="mb-3">
      <Label for={name}>{label}</Label>
      <Input
        type="text"
        name={name}
        id={name}
        placeholder={placeholder || `Ingresa ${label.toLowerCase()}`}
        onChange={(e) => {
          const formatted = formatFormCurrency(e.target.value);
          formik.setFieldValue(name, formatted);
        }}
        onBlur={formik.handleBlur}
        value={formik.values[name]}
        valid={formik.touched[name] && !formik.errors[name]}
        invalid={formik.touched[name] && !!formik.errors[name]}
      />
      <FormFeedback>{formik.errors[name]}</FormFeedback>
    </section>
  );

  return (
    <Modal isOpen={modal} toggle={() => toggle('cancel')} size="lg">
      <ModalHeader toggle={() => toggle('cancel')}>Completar Lead</ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          {errorMessage && (
            <Alert color="danger" className="mb-3">
              {errorMessage}
            </Alert>
          )}

          <p>
            Complete la información final del lead para marcarlo como
            completado.
          </p>

          <Row>
            <Col md={6}>
              <section className="mb-3">
                <Label for="date">Fecha de pago</Label>
                <Input
                  type="date"
                  name="date"
                  id="date"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.date}
                  valid={formik.touched.date && !formik.errors.date}
                  invalid={formik.touched.date && !!formik.errors.date}
                />
                <FormFeedback>{formik.errors.date}</FormFeedback>
              </section>
            </Col>
            <Col md={6}>
              {renderCurrencyInput('price', 'Precio', 'Ingresa el precio')}
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <section className="mb-3">
                <Label for="paymentMethod">Forma de pago</Label>
                <Input
                  type="select"
                  name="paymentMethod"
                  id="paymentMethod"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.paymentMethod}
                  valid={
                    formik.touched.paymentMethod && !formik.errors.paymentMethod
                  }
                  invalid={
                    formik.touched.paymentMethod && !!formik.errors.paymentMethod
                  }
                >
                  <option value="">Selecciona una opción</option>
                  {PAYMENT_METHOD_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{formik.errors.paymentMethod}</FormFeedback>
              </section>
            </Col>
            <Col md={6}>
              <section className="mb-3">
                <Label for="documento">Documento</Label>
                <Input
                  type="select"
                  name="documento"
                  id="documento"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.documento}
                  valid={
                    formik.touched.documento && !formik.errors.documento
                  }
                  invalid={
                    formik.touched.documento && !!formik.errors.documento
                  }
                >
                  <option value="">Selecciona una opción</option>
                  {DOCUMENT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{formik.errors.documento}</FormFeedback>
              </section>
            </Col>
          </Row>

          <hr />
          <h6 className="mb-3">Desagregación de precio</h6>

          <Row>
            <Col md={6}>
              {renderCurrencyInput('valorNeto', 'Valor Neto')}
            </Col>
            <Col md={6}>
              {renderCurrencyInput('iva', 'IVA')}
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              {renderCurrencyInput('comisionCliente', 'Comisión por venta Cliente')}
            </Col>
            <Col md={6}>
              {renderCurrencyInput('comisionProveedor', 'Comisión venta Proveedor')}
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              {renderCurrencyInput('gastoTransferencia', 'Gasto de transferencia')}
            </Col>
            <Col md={6}>
              {renderCurrencyInput('impuestoTransferencia', 'Impuesto transferencia')}
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              {renderCurrencyInput('tag', 'Tag')}
            </Col>
          </Row>

          <hr />

          <section className="mb-3">
            <Label for="comment">Comentarios finales</Label>
            <Input
              type="textarea"
              name="comment"
              id="comment"
              placeholder="Ingrese comentarios sobre el resultado final"
              rows="4"
              className="p-2"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.comment}
              valid={formik.touched.comment && !formik.errors.comment}
              invalid={formik.touched.comment && !!formik.errors.comment}
            />
            <FormFeedback>{formik.errors.comment}</FormFeedback>
          </section>
        </ModalBody>
        <ModalFooter>
          <Button
            type="submit"
            color="primary"
            disabled={!formik.isValid || !formik.dirty || loading}
          >
            {loading ? 'Procesando...' : 'Completar Lead'}
          </Button>{' '}
          <Button color="secondary" onClick={() => toggle('cancel')}>
            Cancelar
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default CompletedForm;
